import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { cerebroScopeFor, fetchTeamRanges, searchPlayersInScope } from "../services/cacheShareServices";
import { legacySportKey } from "../taxonomy";
import { isPositionQuery, matchesQuery, rowId, teamsDataStatus, toImportRow } from "../allocationModel";
import { PROVIDER_UNAVAILABLE, failureOf } from "./useAllocationBoard";
import { useTeamCascade } from "./useTeamCascade";

// A failed roster or search call's toast: the server's own words when the
// data provider is paused with nothing saved, else `fallback`.
const failureToast = (err, fallback) => {
  const failure = failureOf(err);
  return failure.code === PROVIDER_UNAVAILABLE && failure.detail ? failure.detail : fallback;
};

const sourceOf = (team) => (["genius", "cache_teams", "cerebro"].includes(team?.source) ? team.source : "sportsradar");

// A CACHE Partner row linked to a Cerebro player is that player: the name
// search returns the partner row in place of its Cerebro twin, so a roster
// that already holds the Cerebro player hides the partner row too (else the
// coach could add the same athlete twice).
const withLink = (row, raw) => ({ ...row, linkedId: String(raw?.cerebroPlayerId || "") });
const isOnRoster = (existingIds) => (p) => existingIds.has(rowId(p)) || (!!p.linkedId && existingIds.has(p.linkedId));

// A roster row picked from a source team, tagged so the calculator scores
// it against its own team and league when it is imported.
const rosterImport = (row, team, scope) => {
  const source = sourceOf(team);
  return toImportRow(
    {
      ...row,
      source,
      sourceSport: source === "cerebro" ? row.sport || scope.cerebroSports[0] || team.cerebroLeague || "" : "",
      sourceTeamId: source === "genius" ? team.id : row.sourceTeamId || "",
      sourceCompetitionId: source === "genius" ? team.competitionId || null : row.sourceCompetitionId || null,
    },
    source,
  );
};

// The part of a team plan a name or position search may cover. NCAA D-I
// (SportRadar) joins only when a conference or sub-division narrows it:
// unnarrowed, the backend would fetch every D-I roster (about 360 teams)
// per search.
const narrowEnough = (p) => p.dataSource !== "sportsradar" || !!p.conference || !!p.conferenceFilter?.length;

export const scopedSearchPlan = (plan) => {
  if (!plan) return { plan: null, skipsNcaa: false };
  const parts = plan.dataSource === "multi" ? plan.plans : [plan];
  const kept = parts.filter(narrowEnough);
  const skipsNcaa = kept.length < parts.length;
  if (!kept.length) return { plan: null, skipsNcaa };
  if (plan.dataSource !== "multi") return { plan, skipsNcaa };
  return { plan: kept.length === 1 ? kept[0] : { ...plan, plans: kept }, skipsNcaa };
};

// Add player modal (Coach Mode V2 CacheShare™): sport and gender come
// pre-filled, a tier opens the team list and the player search, a team
// narrows the search to its scored roster (filtered as the coach types),
// and picked players stay picked across team changes until they are
// imported. `dataStatus` is what the modal shows a notice for: the picked
// team's roster, else the search's answer, else the team list's (a paused
// provider's saved copy reads "stale").
export const usePlayerImport = ({ defaults, existingIds }) => {
  const cascade = useTeamCascade(defaults);
  const { filters, plan, selectedTeam } = cascade;
  const sport = legacySportKey(filters.sport?.value, filters.gender?.value) || "";
  // "bb" or "fb": which position tokens the search box understands.
  const positionSport = filters.sport?.value;

  const [roster, setRoster] = useState([]);
  const [rosterStatus, setRosterStatus] = useState(null);
  const [isLoadingRoster, setIsLoadingRoster] = useState(false);
  useEffect(() => {
    setRosterStatus(null);
    if (!selectedTeam) {
      setRoster([]);
      setIsLoadingRoster(false);
      return undefined;
    }
    let cancelled = false;
    const scope = cerebroScopeFor(selectedTeam, plan, sport);
    setIsLoadingRoster(true);
    fetchTeamRanges(selectedTeam, { sport, scope, revshareBudget: 0 })
      .then((data) => {
        if (cancelled) return;
        setRoster((data?.results || []).map((r) => withLink(rosterImport(r, selectedTeam, scope), r)).filter((r) => r.sportsRadarId));
        setRosterStatus(data?.dataStatus || null);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(failureToast(err, "Could not load this team's roster. Try again."));
        setRoster([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRoster(false);
      });
    return () => {
      cancelled = true;
    };
    // A picked team keeps its scope: the cascade clears the team whenever
    // the plan changes, so the team is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  const [query, setQuery] = useState("");
  const q = query.trim();
  // A name needs two letters; a position of the sport (G, PG, ... or QB,
  // DE, ...) stands alone.
  const searchable = q.length >= 2 || isPositionQuery(q, positionSport);
  const hasTeam = !!selectedTeam;

  // Without a team, a name or a position searches the whole scope, which
  // can fan out to many rosters: it runs on Enter only, and a newer search
  // aborts the last.
  const searchScope = useMemo(() => scopedSearchPlan(plan), [plan]);
  const scopeKey = searchScope.plan ? JSON.stringify(searchScope.plan) : "";
  const canSearch = !hasTeam && !!searchScope.plan && searchable;
  const [submitted, setSubmitted] = useState(null);
  const search = useCallback(() => {
    if (canSearch) setSubmitted({ term: q, scopeKey });
  }, [canSearch, q, scopeKey]);
  const term = canSearch && submitted?.term === q && submitted?.scopeKey === scopeKey ? q : "";
  const searchKey = term ? `${scopeKey}|${term}` : "";

  const [found, setFound] = useState({ key: "", rows: [], dataStatus: null });
  useEffect(() => {
    if (!searchKey) return undefined;
    const controller = new AbortController();
    const settle = (rows, dataStatus = null) => {
      if (!controller.signal.aborted) setFound({ key: searchKey, rows, dataStatus });
    };
    searchPlayersInScope({ plan: searchScope.plan, q: term, limit: 50, signal: controller.signal })
      .then((data) => settle((data?.results || []).map((r) => withLink(toImportRow(r), r)).filter((r) => r.sportsRadarId), data?.dataStatus || null))
      .catch((err) => {
        if (!controller.signal.aborted) toast.error(failureToast(err, "Search failed. Try again."));
        settle([]);
      });
    return () => controller.abort();
    // The key holds the scope and the term: a new plan object alone must not search again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  // What the player field can do right now without a team.
  const searchState = hasTeam
    ? "team"
    : !searchable
      ? "short"
      : !searchScope.plan
        ? "narrow"
        : !term
          ? "pending"
          : found.key === searchKey
            ? "done"
            : "searching";

  const [selected, setSelected] = useState([]);
  const selectedIds = useMemo(() => new Set(selected.map(rowId)), [selected]);
  const toggle = useCallback(
    (player) => setSelected((list) => (list.some((p) => rowId(p) === rowId(player)) ? list.filter((p) => rowId(p) !== rowId(player)) : [...list, player])),
    [],
  );
  const remove = useCallback((id) => setSelected((list) => list.filter((p) => rowId(p) !== id)), []);

  const onRoster = useMemo(() => isOnRoster(existingIds), [existingIds]);
  const available = useMemo(() => roster.filter((p) => !onRoster(p)), [roster, onRoster]);
  const matches = useMemo(() => {
    if (hasTeam) return available.filter((p) => matchesQuery(p, q, positionSport));
    return found.key === searchKey ? found.rows.filter((p) => !onRoster(p)) : [];
  }, [hasTeam, available, q, positionSport, found, searchKey, onRoster]);

  const listStatus = teamsDataStatus(cascade.teams);
  let dataStatus = listStatus;
  if (hasTeam) dataStatus = rosterStatus || listStatus;
  else if (searchKey && found.key === searchKey && found.dataStatus) dataStatus = found.dataStatus;

  return {
    cascade,
    query,
    setQuery,
    search,
    searchState,
    skipsNcaa: searchScope.skipsNcaa,
    ready: !!plan,
    isLoadingRoster,
    available,
    matches,
    selected,
    selectedIds,
    toggle,
    remove,
    dataStatus,
  };
};
